package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources.Images.HILT_IMAGE_1

@Page("di_hilt")
@Composable
fun HiltDiPage() {
    BlogLayout(
        PageProperties(
            "Hilt",
            ""
        ),
        BlogProperties("Dependency Injection", "Hilt")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Span(Modifier.display(DisplayStyle.Contents).toAttrs()) {
                SpanText(
                    text = "Hilt is a DI library that is a part of Jetpack. It is based on another DI framework called Dagger. Hilt is more of an abstraction layer over Dagger that aims to make it easier to include automated DI in our projects.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Hilt is a dependency injection library for Android that simplifies and automates the process of injecting dependencies into Android components like activities, fragments, and ViewModels.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "It is used in Android development to enhance code maintainability, improve testability, and adhere to best practices.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Hilt reduces the boilerplate code typically associated with Dagger 2 and provides a consistent way to perform dependency injection.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "What is ViewModel?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@HiltViewModel Used to inject dependencies into ViewModels. This annotation simplifies ViewModel injection in Hilt.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " It allows Hilt to manage the lifecycle of the ViewModel and inject the required dependencies into it. The @HiltViewModel annotation is used in conjunction with @Inject in the ViewModel's constructor, and the ViewModel is then provided by Hilt in the Activity or Fragment using the ViewModelProvider.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ViewModel class is a business logic or screen-level state holder. It exposes the state to the UI and encapsulates related business logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Its principal advantage is that it caches state and persists it through configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This means that your UI doesn’t have to fetch data again when navigating between activities, or following configuration changes, such as when rotating the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        @HiltViewModel
                        class ExampleViewModel: ViewModel() {}
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There is a compile-time error, which tells us to inject the constructor of the ViewModel class. So let’s create a default or empty constructor of our ExampleViewModel class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        @HiltViewModel
                        class ExampleViewModel @Inject constructor(): ViewModel() {
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Inject Use Case as Lazy in ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @HiltViewModel
                    class MyViewModel @Inject constructor(
                        private val myUseCase: Lazy<MyUseCase> // Lazy injection of MyUseCase
                    ) : ViewModel() {

                        fun executeUseCase() {
                            myUseCase.get().performAction()  // The use case will be initialized here when called
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "What’s Dependency Injection?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Injection is a design pattern in which an object's dependencies (i.e., the objects it needs to perform its tasks) are supplied to it externally, rather than the object creating the dependencies itself. This helps in achieving loose coupling between objects, making the code more modular, testable, and maintainable. In DI, an object (often called the client) receives other objects (called dependencies) that it requires, rather than creating them directly. These dependencies are usually injected via the constructor, a setter method, or through an interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A form of inversion of control, dependency injection aims to separate the concerns of constructing objects and using them, leading to loosely coupled programs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency injection (DI) is a unique way for classes to acquire references of other classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Client - The object that depends on other objects (dependencies) to function. Dependencies - The objects that are required by the client to perform certain operations. Injector - The component responsible for providing dependencies to the client.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, say that class BananaMilkshake requires the Milk class. Here, BananaMilkShake is dependent on the Milk class. Often these required classes, like Milk, are called dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = HILT_IMAGE_1,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Hilt"
                )
            }

            SpanText(
                text = "Hilt Internals",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt uses bytecode transformation and annotation processing to inject dependencies into Android framework classes like Activity, Application, etc.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While, Dagger processes the annotations at compile time and generates plain Java source code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bytecode transformation modifies the existing code. For example, if we have a class Foo, bytecode transformation can update its properties, change its superclass, etc",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While, annotation processing generates new classes; it cannot modify the existing classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why does Hilt need bytecode transformation?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android framework classes like Activity cannot be instantiated directly; they can only be instantiated by the Android framework.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Therefore, Dagger cannot create instances of them and cannot inject dependencies into them. For injecting the dependencies, we have to write injection code manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt solves that problem by adding the boilerplate injection code into Android framework classes using bytecode transformation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt uses the ASM library for bytecode transformation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dagger Hilt manage the lifecycle of dependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dagger Hilt provides predefined components that are tied to the lifecycle of Android components like Activity, Fragment, and ViewModel. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each Hilt component corresponds to a specific scope, such as @Singleton for the application-level scope, @ActivityRetainedScoped for the activity-level scope, and @ViewModelScoped for the ViewModel-level scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These scopes ensure that dependencies are created and destroyed according to the lifecycle of the associated component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Migrate from Dagger to Dagger Hilt",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Replace the Dagger component with Hilt’s built-in components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Annotate your Application class with @HiltAndroidApp.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Replace @Component injections with @AndroidEntryPoint in your Android components (e.g., Activity, Fragment).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Move @Module annotations and specify the appropriate component using @InstallIn.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Test the application to ensure that the dependencies are being provided correctly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Hilt Annotation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Application",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The main goal around DI is to be able to automatically know how to provide an instance of some type when it’s required, and for that, we shall provide some context levels where our dependencies shall exist our when should they be created(lifecycle).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The application is the topmost level where we can define the lifecycle of a dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Basically we want to have a topmost container for all lifecycles to exist, and this application is responsible to be a container that instantiates dependencies in the application level, so we need to customize our own application to have DI containers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @HiltAndroidApp
                    class PokeAPIApplication: Application()
                    """.trimIndent()
            )

            SpanText(
                text = "@HiltAndroidApp",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We need to apply this annotation to our application class, which triggers the code generation and creates the base component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation triggers Hilt’s code generation, including a base class for your application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The application container is the parent container of the app, which means that other containers (from Android components) can access the dependencies that it provides.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation triggers the generation of the necessary Dagger components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@AndroidEntryPoint",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt creates a dependency container to the Android component that it was assigned so it can inject the dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation generates an individual Hilt component for each Android component so they can inject necessary dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To enable dependency injection in Android components, such as activities and fragments, developers use the @AndroidEntryPoint annotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation informs Hilt to inject the required dependencies into the annotated Android component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @AndroidEntryPoint
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            setContent {
                            
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@Inject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is used in a constructor, field, or method where the dependency is requested. If we want instances a class to be injected, we need to declare a variable of that type and annotate it with the @Inject annotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ViewModelInject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ViewModelInject is an annotation in Hilt that simplifies the process of injecting dependencies into ViewModels.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModels are components used to manage and retain UI-related data, but they often require dependencies like repositories, network services, or databases.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Manually injecting these dependencies into ViewModels can be challenging and may lead to complex, tightly coupled code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Use Hilt Multibinding",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In DI systems like Dagger and Hilt, we often define bindings that specify how to provide an instance of a class. Normally, we can bind a single instance for a given type, but what if you want to provide multiple implementations or variants of the same type?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is where multibinding comes in. It allows you to inject a collection (such as a Set or Map) of instances of the same type, making it easier to handle dynamic or variable data sets where multiple implementations are required.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To use multibinding in Hilt, we need to leverage special annotations and Kotlin collections (or Java collections) that support multiple bindings.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@IntoSet",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @IntoSet annotation is used when you want to contribute multiple objects of the same type to a Set.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Each object is treated as an individual element of the Set.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is useful when you have multiple implementations of a dependency and you want all of them to be injected as a Set.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Imagine you are building an app that sends notifications. You want to handle multiple types of notifications, such as Email and SMS. You can create a NotificationSender interface, and then implement it with two classes: EmailSender and SmsSender.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //https://github.com/shamim-emon/Multi-Binding-Hilt-Demo
                    
                    import timber.log.Timber
                    import javax.inject.Inject

                    interface NotificationSender {
                        fun send(message: String)
                    }

                    class EmailSender @Inject constructor() : NotificationSender {
                        override fun send(message: String) {
                           
                        }
                    }

                    class SmsSender @Inject constructor() : NotificationSender {
                        override fun send(message: String) {
                            
                        }
                    }
                    
                    @Module
                    @InstallIn(SingletonComponent::class)
                    interface NotificationModule {
                        @Binds
                        @IntoSet
                        fun bindEmailSender(emailSender: EmailSender): NotificationSender

                        @Binds
                        @IntoSet
                        fun bindSmsSender(smsSender: SmsSender): NotificationSender
                    }
                    
                    const val TAG_SET = "Multi-Binding-Hilt-set"

                    @AndroidEntryPoint
                    class MainActivity : AppCompatActivity() {
                        @Inject lateinit var notificationSenders: Set<@JvmSuppressWildcards NotificationSender>

                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContentView(R.layout.activity_main)
                            ViewCompat.setOnApplyWindowInsetsListener(findViewById(R.id.main)) { v, insets ->
                                val systemBars = insets.getInsets(WindowInsetsCompat.Type.systemBars())
                                v.setPadding(systemBars.left, systemBars.top, systemBars.right, systemBars.bottom)
                                insets
                            }

                            notificationSenders.forEach { it.send("Hello, this is a notification!") }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object RepositoryModule {
                    @Provides
                        @IntoSet
                        fun provideRemoteUserRepository(): UserRepository {
                            return RemoteUserRepository()
                        }
                        
                        @Provides
                        @IntoSet
                        fun provideLocalUserRepository(): UserRepository {
                            return LocalUserRepository()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Set",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In your component (like a BroadcastReceiver), you can inject the Set of repositories like this:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @EntryPoint
                    @InstallIn(SingletonComponent::class)
                    interface MyBroadcastReceiverEntryPoint {
                        fun userRepositories(): Set<UserRepository>
                    }

                    class MyBroadcastReceiver : BroadcastReceiver() {
                    
                        override fun onReceive(context: Context, intent: Intent?) {
                        
                        }    
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@IntoMap",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @IntoMap annotation is used when you want to bind multiple implementations of a dependency into a Map. Each object is associated with a key (which can be a string, an enum, or another type), allowing you to differentiate between different instances of the same type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Module
                    @InstallIn(SingletonComponent::class)
                    interface PaymentModule {
                        @Binds
                        @IntoMap
                        @StringKey("payPal")
                        fun bindPayPalProcessor(payPalProcessor: PayPalProcessor): PaymentProcessor

                        @Binds
                        @IntoMap
                        @StringKey("stripe")
                        fun bindStripeProcessor(stripeProcessor: StripeProcessor): PaymentProcessor


                        @Binds
                        @IntoMap
                        @StringKey("googlePay")
                        fun bindGooglePayProcessor(googlePayProcessor: GooglePayProcessor): PaymentProcessor

                    }
                    
                    const val TAG_MAP = "Multi-Binding-Hilt-map"

                    @AndroidEntryPoint
                    class MainActivity : AppCompatActivity() {
                        
                        @Inject lateinit var paymentProcessors: Map<String,@JvmSuppressWildcards PaymentProcessor>

                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContentView(R.layout.activity_main)
                            ViewCompat.setOnApplyWindowInsetsListener(findViewById(R.id.main)) { v, insets ->
                                val systemBars = insets.getInsets(WindowInsetsCompat.Type.systemBars())
                                v.setPadding(systemBars.left, systemBars.top, systemBars.right, systemBars.bottom)
                                insets
                            }

                            paymentProcessors["payPal"]?.processPayment(100.0)
                            paymentProcessors["stripe"]?.processPayment(10.0)
                            paymentProcessors["googlePay"]?.processPayment(22.0)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object RepositoryModule {

                    @Provides
                        @IntoMap
                        @StringKey("remote")
                        fun provideRemoteUserRepository(): UserRepository {
                            return RemoteUserRepository()
                        }
                        @Provides
                        @IntoMap
                        @StringKey("local")
                        fun provideLocalUserRepository(): UserRepository {
                            return LocalUserRepository()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@InstallIn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Hilt module class should also be annotated with @InstallIn to specify the scope of the module. For example, if we annotate the module with @InstallIn(ActivityComponent::class), then the module will bind to the activity lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We annotate with InstallIn specifying it's a singleton component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "his annotation tells us what is the context we want our module and those dependencies to be created. When we we specify a SingletonComponent we say we want each dependency to be instantiated at the start of the application lifecycle and we want that to be reused. We could provide other context like a ViewModelComponent to exist just in the HiltViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object PokemonListModule {
                        @Provides
                        fun provideRetrofitClient(): Retrofit {
                            return Retrofit.Builder()
                                .baseUrl("")
                                .addConverterFactory(GsonConverterFactory.create())
                                .client(OkHttpClient())// Add converter factory for Gson
                                .build()
                        }

                        @Provides
                        fun provideAPIService(retrofitClient: Retrofit): APIService {
                            return retrofitClient.create(APIService::class.java)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Store-ecommerceApp: https://github.com/ymatinfard/HappyStore-ecommerceApp
                    //without database using filter, favourite, cart add, total calculation
                    
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object ApplicationStateModule {

                        @Provides
                        @Singleton
                        fun provideApplicationState(): Store<ApplicationState> = Store(ApplicationState())
                    }
                    
                    class Store<T>(initialState: T) {
                        private val _state = MutableStateFlow(initialState)
                        val state : StateFlow<T> = _state.asStateFlow()

                        private val mutex = Mutex()

                        suspend fun update(updateBlock: (T) -> T) = mutex.withLock {
                            val newState = updateBlock(_state.value)
                            _state.value = newState
                        }

                        suspend fun read(readBlock: (T) -> Unit) = mutex.withLock {
                            readBlock(_state.value)
                        }
                    }
                    
                    data class ApplicationState(
                        val products: List<Product> = emptyList(),
                        val productFilterInfo: ProductFilterInfo = ProductFilterInfo(),
                        val favoriteProductId: Set<Int> = emptySet(),
                        val expandedProductIds: Set<Int> = emptySet(),
                        val inCartProductIds: Set<Int> = emptySet(),
                        val inCartProductQuantity: Map<Int, Int> = emptyMap(),
                    ) {
                        data class ProductFilterInfo(
                            val filters: Set<Filter> = emptySet(),
                            val selectedFilter: Filter? = null,
                        )
                    }
                    
                    class ProductExpandUpdater @Inject constructor() {
                        operator fun invoke(id: Int, appState: ApplicationState): ApplicationState {
                            val updatedExpandedProductIds = if (appState.expandedProductIds.contains(id)) {
                                appState.expandedProductIds - id
                            } else {
                                appState.expandedProductIds + id
                            }
                            return appState.copy(expandedProductIds = updatedExpandedProductIds)
                        }
                    }
                    
                    class ProductFavoriteUpdater @Inject constructor() {
                        operator fun invoke(id: Int, appState: ApplicationState): ApplicationState {
                            val updateFavIds = if (appState.favoriteProductId.contains(id)) {
                                appState.favoriteProductId - id
                            } else {
                                appState.favoriteProductId + id
                            }
                            return appState.copy(favoriteProductId = updateFavIds)
                        }
                    }
                    
                    @HiltViewModel
                    class HappyStoreViewModel @Inject constructor(
                        val store: Store<ApplicationState>,
                        private val getProductsUseCase: GetProductsUseCase,
                        private val categoryFilterGeneratorUseCase: ProductCategoryFilterGeneratorUseCase,
                        private val productListReducer: ProductListReducer,
                        private val productFavoriteUpdater: ProductFavoriteUpdater,
                        private val productExpandUpdater: ProductExpandUpdater,
                        private val productFilterSelectionUpdater: ProductFilterSelectionUpdater,
                        private val productInCartItemUpdater: ProductInCartItemUpdater,
                        val productListUIStateGenerator: ProductListUIStateGenerator,
                    ) : ViewModel() {
                    
                        private fun getProducts() {
                            viewModelScope.launch {
                                when (val result = getProductsUseCase()) {
                                    is Result.Success -> {
                                        store.update { applicationState ->
                                            return@update applicationState.copy(
                                                products = result.data,
                                                productFilterInfo = ProductFilterInfo(
                                                    filters = categoryFilterGeneratorUseCase(
                                                        result.data
                                                    ),
                                                    selectedFilter = applicationState.productFilterInfo.selectedFilter
                                                )
                                            )
                                        }
                                    }

                                    is Result.Error -> TODO()
                                }
                            }
                        }
                        
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Binding",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt needs to know how to provide instances of the necessary dependencies from the corresponding component. A binding contains the information necessary to provide instances of a type as a dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bindings are typically done in Hilt Modules using annotations like @Provides or @Binds.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Binds (Interface-based binding)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor injection works well when your dependency can be directly instantiated, but what if you’re working with interfaces? Interfaces are often used in Android apps to ensure loose coupling and increase flexibility. However, they can’t be directly instantiated by Hilt, so this is where @Binds comes in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Binds allows you to map an interface to a concrete implementation(class that implements your iterface), and Hilt will automatically inject that implementation wherever the interface is needed. It works best when you don’t need additional logic to provide the dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When to use: Constructor injection works well when your dependency can be directly instantiated, but what if you're working with interfaces? Interfaces are often used in Android apps to ensure loose coupling and increase flexibility. However, they can't be directly instantiated by Hilt, so this is where Binds comes in. If you have an interface or abstract class and want to bind it to its concrete implementation. @Binds allows you to map an interface to a concrete implementation (class that implements your interface), and Hilt will automatically inject that implementation wherever the interface is needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Provides (Method-based binding)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is used on the methods that are inside the module class and provides the dependency objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each function inside this scope should have the Provides annotation, which indicates that wherever we inject one of the returned types of the functions, the corresponding providing method should be called to provide that specific instance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When to use: When you need to perform custom initialization logic for creating an instance, such as passing parameters or creating instances of classes that can’t be directly injected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Scopes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt uses Dagger’s scope annotations (like @Singleton) to manage the lifecycle of dependencies. Each component has a corresponding scope that determines the lifecycle of the dependencies it provides.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ContributesAndroidInjector",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ContributesAndroidInjector annotation was introduced for generating Subcomponent. But it also brings boilerplate code in our huge project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ViewModelInject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is used on the methods that are inside the module class and provides the dependency objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "we need to include @ViewModelInject in the ViewModel constructor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        class MainViewModel @ViewModelInject constructor(
                         private val Repository: Repository
                        ): ViewModel() {
                            //…
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now, in Android components like Activity and Fragment, we can directly use them without @inject but with the by viewModels() delegate function. Have a look:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                         private val mainViewModel: MainViewModel by viewModels()
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you want to use the Activity-level ViewModel, we need to apply the by activityViewModels() delegate function instead of by viewModels().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Constructor Injection",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hilt encourages constructor injection, and this includes ViewModels.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With @ViewModelInject, you can annotate the constructor of your ViewModel class to indicate that it should be injected with its required dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This results in cleaner, more modular code and improved testability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Now Create Hilt Modules",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "A Hilt module is a class that is annotated with @Module. Like a Dagger module, it informs Hilt how to provide instances of certain types. Unlike Dagger modules, you must annotate Hilt modules with @InstallIn to tell Hilt which Android class each module will be used or installed in.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "@Module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Classes annotated with Module allow us to tell Hilt how to provide dependencies. We annotate our object as a Module in order to declare it’s the type that will provide the dependencies we need to inject across our project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is used on the classes that create the objects of dependency classes (for the classes that you don’t own — for example, third-party library classes like OkHttp or Retrofit).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to mark the class as a Dagger Hilt module. Modules are used to group related bindings (provided dependencies) together.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@DefineComponet",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Are containers for a group of dependencies. They use modules and ensure the injection of dependencies happen at the right time. They have @DefineComponet annotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@InstallIn(SingletonComponent::class)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @InstallIn annotation in Hilt is used to specify where a module should be installed within the dependency injection graph.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation specifies the Hilt component where the bindings in the module should be installed. In your case, it's the SingletonComponent, which means the dependencies will have a singleton scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It defines the scope or component that the module and its provided dependencies will be available to. In simple terms, it tells Hilt which component or lifecycle should be associated with a module, and thus, where the provided dependencies will be injected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @InstallIn annotation is mandatory when defining Hilt modules because it dictates the lifetime and visibility of the dependencies provided by the module.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "object AppModule",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This declares an object named AppModule, which contains the Dagger Hilt module.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Provides",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to annotate methods inside a Dagger Hilt module, indicating that the annotated method provides a dependency. When Hilt can’t directly inject a class through its constructor (for example, third-party libraries), you can use the @Provides annotation in a module to tell Hilt how to create an instance of that class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object AppModule {

                    @Provides
                        fun provideApiService(): ApiService {
                            return ApiService()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@Scoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Annotations that control how long dependencies live in your app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@AssistedInject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A way to create things with runtime data more easily.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ActivityScoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates one instance per activity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ActivityRetainedScoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The dependency lives across configuration changes but is tied to an activity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@FragmentScoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The dependency lives as long as the fragment does.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ViewModelScoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ties the lifecycle of a dependency to a ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Singleton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation specifies that the provided dependency should have a singleton scope, meaning that only one instance of the provided dependency will be created and reused throughout the application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                KotlinCode(
                    code = """
                        fun provideApplicationContext(application: Application): Context
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This method provides the application context as a dependency. It takes an Application instance as a parameter and returns the application context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //ViewModel access
                    class SearchViewModel @Inject constructor(
                        private val searchRepository: SearchRepository,
                        private val dispatcherProvider: DispatcherProvider,
                        private val networkHelper: NetworkHelper,
                    ) : ViewModel() {
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun SearchScreenRoute(
                        onNewsClick: (url: String) -> Unit,
                        searchViewModel: SearchViewModel = hiltViewModel()
                    ) {
                      val searchUiState: UiState<List<Article>> by searchViewModel.searchUiState.collectAsStateWithLifecycle()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import javax.inject.Qualifier
                    import kotlin.annotation.AnnotationRetention.RUNTIME

                    @Qualifier
                    @Retention(RUNTIME)
                    annotation class Dispatcher(val dispatchers: SDUIDispatchers)

                    enum class SDUIDispatchers {
                        IO, MAIN
                    }
                    
                    import dagger.Module
                    import dagger.Provides
                    import dagger.hilt.InstallIn
                    import dagger.hilt.components.SingletonComponent
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    @[Module InstallIn(SingletonComponent::class)]
                    object DispatchersModule {
                        @[Provides Dispatcher(SDUIDispatchers.IO)]
                        fun providesIoDispatcher(): CoroutineDispatcher = Dispatchers.IO
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //qualifier.kt
                    import javax.inject.Qualifier

                    @Qualifier
                    @Retention(AnnotationRetention.RUNTIME)
                    annotation class DatabaseName

                    @Qualifier
                    @Retention(AnnotationRetention.RUNTIME)
                    annotation class NetworkAPIKey

                    @Qualifier
                    @Retention(AnnotationRetention.RUNTIME)
                    annotation class BaseUrl

                    //DI
                    @Module
                    @InstallIn(SingletonComponent::class)
                    class ApplicationModule {
                        @BaseUrl
                        @Provides
                        fun provideBaseUrl(): String = AppConstant.BASE_URL


                        @NetworkAPIKey
                        @Provides
                        fun provideApiKey(): String = AppConstant.API_KEY

                        @DatabaseName
                        @Provides
                        fun provideDatabaseName(): String = AppConstant.DATABASE_NAME

                        @Provides
                        @Singleton
                        fun provideApiKeyInterceptor(@NetworkAPIKey apiKey: String): ApiKeyInterceptor =
                            ApiKeyInterceptor(apiKey)

                        @Provides
                        @Singleton
                        fun provideDispatcher(): DispatcherProvider = DefaultDispatcherProvider()

                        @Provides
                        @Singleton
                        fun provideLogger(): Logger = AppLogger()

                        @Provides
                        @Singleton
                        fun provideGsonConverterFactory(): GsonConverterFactory = GsonConverterFactory.create()
                    }

                    //Object DI
                    @Module
                    @InstallIn(SingletonComponent::class)
                    object XModule {

                      @Provides
                      @Singleton
                      fun provideXThing(): XThing { .. }

                      @Provides
                      @Singleton
                      fun provideYThing(xthing: XThing) { .. }

                    }

                    //DispatcherProvider.kt
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    interface DispatcherProvider {
                        val main: CoroutineDispatcher
                        val io: CoroutineDispatcher
                        val default: CoroutineDispatcher

                    }

                    class DefaultDispatcherProvider : DispatcherProvider {
                        override val main: CoroutineDispatcher
                            get() = Dispatchers.Main

                        override val io: CoroutineDispatcher
                            get() = Dispatchers.IO

                        override val default: CoroutineDispatcher
                            get() = Dispatchers.Default
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/Felix-Kariuki/SDUI-Compose
                    
                    @HiltViewModel
                    class HomeViewModel @Inject constructor(
                        repository: GamesRepository
                    ) : ViewModel() {
                        val gamesUi: StateFlow<ScreenUi?> = repository.fetchGamesUi()
                            .flatMapLatest { response -> flowOf(response.getOrNull()) }
                            .filterNotNull()
                            .map {
                                ScreenUi(
                                    version = it.version,
                                    components = it.buildUiComponentList()
                                )
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5000L),
                                initialValue = null
                            )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/Felix-Kariuki/MoneySwift
                    
                    import androidx.arch.core.executor.testing.InstantTaskExecutorRule
                    import kotlinx.coroutines.ExperimentalCoroutinesApi
                    import org.junit.Rule

                    abstract class BaseViewModelTest {
                        @OptIn(ExperimentalCoroutinesApi::class)
                        @get:Rule
                        val mainDispatcherRule = MainCoroutineRule()

                        @get:Rule
                        val instantTaskExecutor: InstantTaskExecutorRule = InstantTaskExecutorRule()
                    }
                    
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.ExperimentalCoroutinesApi
                    import kotlinx.coroutines.test.TestDispatcher
                    import kotlinx.coroutines.test.UnconfinedTestDispatcher
                    import kotlinx.coroutines.test.resetMain
                    import kotlinx.coroutines.test.setMain
                    import org.junit.rules.TestWatcher
                    import org.junit.runner.Description

                    @ExperimentalCoroutinesApi
                    class MainCoroutineRule(val dispatcher: TestDispatcher = UnconfinedTestDispatcher()) :
                        TestWatcher() {

                        override fun starting(description: Description?) = Dispatchers.setMain(dispatcher)

                        override fun finished(description: Description?) = Dispatchers.resetMain()
                    }
                    
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class EcommerceViewmodelTest : BaseViewModelTest() {

                        private val getListingByIdRepo = GetListingByIdTestRepository()
                        private val getListingsRepo = GetListingsTestRepository()
                        private val stripeRepo = StripeTestRepository()
                        private lateinit var viewModel: EcommerceViewModel

                        @Before
                        fun setup() {
                            viewModel = EcommerceViewModel(
                                getListingsRepository = getListingsRepo,
                                getListingByIdRepository = getListingByIdRepo,
                                stripePaymentRepository = stripeRepo,
                            )
                        }

                        @Test
                        fun `test get item by id updates ui respectively with success state`() = runTest {
                            val collectJob = launch(UnconfinedTestDispatcher()) {
                                viewModel.state.collect()
                            }

                            viewModel.getAListingById(1)
                            val uiState = viewModel.state.value
                            assertFalse(uiState.isLoading)
                            Assert.assertNull(uiState.errorMsg)
                            Truth.assertThat(uiState.singleListing).isEqualTo(
                                com.flexcode.ecommerce.presentation.components.sampleItem,
                            )
                            collectJob.cancel()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Multiple bindings for the same type",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Hilt, you can provide multiple bindings for the same type by using qualifiers. Qualifiers are custom annotations that allow you to differentiate between different implementations of the same type. You define custom qualifiers for each implementation and use the @Provides or @Binds annotation with the qualifier to bind the specific implementation to a type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, Hilt cannot decide which implementation to inject if you have multiple providers for the same type. Using qualifiers helps Hilt distinguish between these implementations and ensures the correct one is injected where needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Hilt handle dependency injection in a multi-module project?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multi-module project, Hilt allows you to define modules and components specific to each module. You can use the @InstallIn annotation to specify the component (e.g., SingletonComponent, ActivityComponent) in which the module should be installed. This modular approach allows each feature module to have its dependencies managed independently while still sharing common dependencies from the app module.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Dagger Hilt manage the lifecycle of dependencies?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dagger Hilt provides predefined components that are tied to the lifecycle of Android components like Activity, Fragment, and ViewModel. Each Hilt component corresponds to a specific scope, such as @Singleton for the application-level scope, @ActivityRetainedScoped for the activity-level scope, and @ViewModelScoped for the ViewModel-level scope. These scopes ensure that dependencies are created and destroyed according to the lifecycle of the associated component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is Dependency Graph?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A dependency graph is like a map showing how different parts of your app depend on each other.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Hilt or Dagger, this graph tells the framework what dependencies (objects or services) are needed, where they are used, and how to provide them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example: If your app needs a Database object, the graph shows where and how to create or get it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

//https://itnext.io/dependency-injection-in-android-the-complete-guide-to-writing-maintainable-code-2a9362c5e687
//https://github.com/rahul3310/RecipeApp (Hilt Worker)
//https://www.linkedin.com/pulse/dependency-injection-compose--wnqqc/?trackingId=eta2IsmPQ2%2B198t5OYM7ig%3D%3D
//https://medium.com/@zorbeytorunoglu/depencency-injection-di-on-android-1dade6c22711
//https://www.linkedin.com/pulse/dagger-hilt-withandroid-anand-gaur-asz1c/?trackingId=CzBDVtb91H%2BeDzethDksPw%3D%3D

//   hilt {
//        enableAggregatingTask = false
//    }